import {
  ReadingsDataRawResponse,
  ReadingsDataRawStateResponse,
  useChartData,
} from "@inrange/building-manager-api-client";
import { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { DateRangePicker } from "../../../components";
import Loading from "../../Loading";
import StateDataTable from "./StateDataTable";
import { Formatters } from "./types";

interface SiteReadingsRawStateProps {
  siteId: string;
  selectedEsysUuid: string | undefined;
  formatters: Formatters;
  initialStartDate: string;
  initialEndDate: string;
  urlParamsConsumed: boolean;
  onUrlParamsConsumed: () => void;
  onDateChange: (startDate: string, endDate: string) => void;
}

const SiteReadingsRawState = ({
  siteId,
  selectedEsysUuid,
  formatters,
  initialStartDate,
  initialEndDate,
  urlParamsConsumed,
  onUrlParamsConsumed,
  onDateChange,
}: SiteReadingsRawStateProps) => {
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  const [timePeriod, setTimePeriod] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Fetch raw state data range (reuses the raw data range endpoint)
  const stateDataRangeQuery = useChartData({
    dataType: "readings_data_raw",
    options: {},
    siteId: siteId,
    enabled: true,
  });

  // Calculate date options for state data (same as raw data)
  const stateDataOptions = useMemo(() => {
    if (!startDate || !endDate) return {};

    const start = new Date(startDate);
    const end = new Date(endDate);
    const endPlusOne = new Date(end);
    endPlusOne.setDate(endPlusOne.getDate() + 1);

    const daysDiff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysDiff > 90) {
      const truncatedStart = new Date(end);
      truncatedStart.setDate(truncatedStart.getDate() - 89);
      return {
        startDate: truncatedStart.toISOString(),
        endDate: endPlusOne.toISOString(),
      };
    }

    return {
      startDate: start.toISOString(),
      endDate: endPlusOne.toISOString(),
    };
  }, [startDate, endDate]);

  // Fetch raw state data with date range filter
  const stateDataQuery = useChartData({
    dataType: "readings_data_raw_state",
    options: stateDataOptions,
    siteId: siteId,
    enabled: Object.keys(stateDataOptions).length > 0,
  });

  // stateDataQuery is disabled until a date range is set (derived from the
  // always-enabled range query); use isInitialLoading so a site with no raw
  // state data doesn't hang on the spinner forever under @tanstack/react-query
  // v4 (a disabled query reports isLoading: true).
  const isLoading =
    stateDataRangeQuery.isLoading || stateDataQuery.isInitialLoading;
  const queryError = stateDataQuery.error || stateDataRangeQuery.error;
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Unknown error"
    : null;

  // Calculate available time periods from the data
  const availableTimePeriods = useMemo(() => {
    const periods: string[] = [];
    periods.push("last7days");
    periods.push("all");

    let data: { x: string }[] = [];
    if (stateDataRangeQuery.data) {
      const rawData = stateDataRangeQuery.data as ReadingsDataRawResponse;
      const filteredSeries = selectedEsysUuid
        ? rawData.series.filter((s) => s.esysUuid === selectedEsysUuid)
        : rawData.series;
      const timestamps = new Set<string>();
      filteredSeries.forEach((s) => {
        s.data.x.forEach((timestamp) => {
          timestamps.add(new Date(timestamp).toISOString().split("T")[0]);
        });
      });
      data = Array.from(timestamps).map((x) => ({ x }));
    }

    if (data.length > 0) {
      const yearMonths = new Map<string, Set<string>>();

      data.forEach((point) => {
        const dateStr = formatters.toDateString(point.x);
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(0, 7);

        if (!yearMonths.has(year)) {
          yearMonths.set(year, new Set());
        }
        yearMonths.get(year)!.add(month);
      });

      const sortedYears = Array.from(yearMonths.keys()).sort().reverse();
      sortedYears.forEach((year) => {
        periods.push(year);
        const months = Array.from(yearMonths.get(year)!).sort().reverse();
        months.forEach((month) => periods.push(month));
      });

      periods.push("custom");
    }

    return periods;
  }, [stateDataRangeQuery.data, selectedEsysUuid, formatters]);

  // Helper function to find matching time period for given dates
  const findMatchingPeriod = (
    newStartDate: string,
    newEndDate: string,
    periods: string[]
  ): string | undefined => {
    return periods.find((period) => {
      if (
        period === "all" ||
        period === "custom" ||
        period === "last7days" ||
        period === "last30days"
      )
        return false;

      if (period.length === 4) {
        return (
          newStartDate === `${period}-01-01` && newEndDate === `${period}-12-31`
        );
      } else if (period.length === 7) {
        const [year, month] = period.split("-");
        const monthNum = parseInt(month, 10);
        const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
        return (
          newStartDate === `${period}-01` &&
          newEndDate === `${period}-${lastDay.toString().padStart(2, "0")}`
        );
      }
      return false;
    });
  };

  // Initialize with default period when data is available
  useEffect(() => {
    if (!isInitialized && stateDataRangeQuery.data) {
      if (initialStartDate && initialEndDate && !urlParamsConsumed) {
        setStartDate(initialStartDate);
        setEndDate(initialEndDate);
        const matchingPeriod = findMatchingPeriod(
          initialStartDate,
          initialEndDate,
          availableTimePeriods
        );
        setTimePeriod(matchingPeriod || "custom");
        setIsInitialized(true);
        onUrlParamsConsumed();
        return;
      }

      setTimePeriod("last7days");

      const rawData = stateDataRangeQuery.data as ReadingsDataRawResponse;
      if (rawData.availableEnd) {
        const latestDate = new Date(rawData.availableEnd);
        const endDateObj = new Date(latestDate);
        const startDateObj = new Date(latestDate);
        startDateObj.setDate(startDateObj.getDate() - 6);

        const formatDate = (date: Date) => {
          return date.toISOString().split("T")[0];
        };

        const newStartDate = formatDate(startDateObj);
        const newEndDate = formatDate(endDateObj);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        onDateChange(newStartDate, newEndDate);
      }

      setIsInitialized(true);
    }
  }, [
    stateDataRangeQuery.data,
    isInitialized,
    initialStartDate,
    initialEndDate,
    availableTimePeriods,
    urlParamsConsumed,
    onUrlParamsConsumed,
    onDateChange,
  ]);

  // Handle time period changes
  useEffect(() => {
    if (timePeriod === "last7days") {
      if (stateDataRangeQuery.data) {
        const rawData = stateDataRangeQuery.data as ReadingsDataRawResponse;
        if (rawData.availableEnd) {
          const latestDate = new Date(rawData.availableEnd);
          const endDateObj = new Date(latestDate);
          const startDateObj = new Date(latestDate);
          startDateObj.setDate(startDateObj.getDate() - 6);

          const formatDate = (date: Date) => {
            return date.toISOString().split("T")[0];
          };

          const newStartDate = formatDate(startDateObj);
          const newEndDate = formatDate(endDateObj);
          setStartDate(newStartDate);
          setEndDate(newEndDate);
          onDateChange(newStartDate, newEndDate);
        }
      }
    } else if (timePeriod === "all") {
      if (stateDataRangeQuery.data) {
        const rawData = stateDataRangeQuery.data as ReadingsDataRawResponse;
        if (rawData.availableStart && rawData.availableEnd) {
          const start = new Date(rawData.availableStart);
          const end = new Date(rawData.availableEnd);
          const daysDiff = Math.ceil(
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysDiff > 90) {
            const truncatedStart = new Date(end);
            truncatedStart.setDate(truncatedStart.getDate() - 89);
            const newStartDate = truncatedStart.toISOString().split("T")[0];
            const newEndDate = end.toISOString().split("T")[0];
            setStartDate(newStartDate);
            setEndDate(newEndDate);
            onDateChange(newStartDate, newEndDate);
          } else {
            const newStartDate = start.toISOString().split("T")[0];
            const newEndDate = end.toISOString().split("T")[0];
            setStartDate(newStartDate);
            setEndDate(newEndDate);
            onDateChange(newStartDate, newEndDate);
          }
        }
      }
    }
  }, [timePeriod, stateDataRangeQuery.data, onDateChange]);

  // Handle time period selection
  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);

    if (period === "last7days" || period === "all") {
      // Dates will be calculated by the useEffect
    } else if (period === "custom") {
      // Keep existing dates
    } else if (period.includes("-") && period.length === 7) {
      const [year, month] = period.split("-");
      const monthNum = parseInt(month, 10);
      const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
      const newStartDate = `${period}-01`;
      const newEndDate = `${period}-${lastDay.toString().padStart(2, "0")}`;
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onDateChange(newStartDate, newEndDate);
    } else {
      const newStartDate = `${period}-01-01`;
      const newEndDate = `${period}-12-31`;
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onDateChange(newStartDate, newEndDate);
    }
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onDateChange(newStartDate, newEndDate);

    const matchingPeriod = findMatchingPeriod(
      newStartDate,
      newEndDate,
      availableTimePeriods
    );
    setTimePeriod(matchingPeriod || "custom");
  };

  const hasData =
    stateDataQuery.data &&
    (stateDataQuery.data as ReadingsDataRawStateResponse).stateTransitions
      .length > 0;

  return (
    <>
      <Row className="mb-3">
        <Col sm={3} className="mb-3">
          <Form.Group>
            <Form.Label>Time Period</Form.Label>
            <Form.Select
              value={timePeriod}
              onChange={(e) => handleTimePeriodChange(e.target.value)}
            >
              {availableTimePeriods.map((period) => {
                let label = period;
                if (period === "last7days") {
                  label = "Last 7 days";
                } else if (period === "all") {
                  label = "All available";
                } else if (period === "custom") {
                  label = "Custom";
                }
                return (
                  <option key={period} value={period}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col sm={6} className="mb-3">
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <DateRangePicker
              value={{
                start: startDate ? new Date(startDate) : null,
                end: endDate ? new Date(endDate) : null,
              }}
              onChange={({ start, end }) => {
                const newStart = start ? start.toISOString().split("T")[0] : "";
                const newEnd = end ? end.toISOString().split("T")[0] : "";
                handleDateChange(newStart, newEnd);
              }}
              mode="date"
            />
          </Form.Group>
        </Col>
      </Row>

      {isLoading && <Loading label="Loading readings data..." height="60vh" />}

      {error && (
        <Row>
          <Col>
            <div className="alert alert-danger" role="alert">
              Error loading readings data: {error}
            </div>
          </Col>
        </Row>
      )}

      {!isLoading && !error && !hasData && (
        <Row>
          <Col>
            <div className="alert alert-info" role="alert">
              No state data available for this site
            </div>
          </Col>
        </Row>
      )}

      {!isLoading &&
        !error &&
        hasData &&
        isInitialized &&
        stateDataQuery.data && (
          <StateDataTable
            data={stateDataQuery.data as ReadingsDataRawStateResponse}
            selectedEsysUuid={selectedEsysUuid}
            startDate={startDate}
            endDate={endDate}
            formatters={formatters}
          />
        )}
    </>
  );
};

export default SiteReadingsRawState;
